import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';

// 발인일로부터 이 일수를 "초과"하면 셀프 온라인 주문을 마감한다. (마감 후엔 전화 주문만)
export const GIFT_STORE_SELF_ORDER_DAYS = 30;

export type GiftStoreAccessStatus =
  | 'loading'
  | 'no-bugo' // bugoId 자체가 없음
  | 'invalid' // bugoId는 있으나 서버에 해당 부고가 없음(200+null) / 조회 실패
  | 'open'
  | 'closed';

export interface GiftStoreAccessResult {
  access: GiftStoreAccessStatus;
  deceasedName: string | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// 시각을 무시하고 날짜(자정) 기준으로 비교해 시간대/시분에 의한 오차를 없앤다.
const startOfDay = (date: Date): number => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * bugoId로 부고를 조회해 답례품 스토어의 접근 상태를 판정한다.
 * - bugoId 없음 → 'no-bugo' (부고 링크 안내)
 * - 조회 실패 / 부고 없음(서버 200+null) → 'invalid' (유효하지 않은 링크 안내)
 * - 부고 로딩 중 → 'loading' (마감 화면 깜빡임 방지)
 * - 발인일 미정/미래 or 발인 후 30일 이내 → 'open'
 * - 발인 후 30일 초과 → 'closed'
 *
 * 발인일 데이터가 없을 때는 막지 않고 열어둔다(정상 유저 차단 방지).
 */
export const useGiftStoreAccess = (bugoId: string | null): GiftStoreAccessResult => {
  useBugoHook(bugoId ?? '', !bugoId);
  const { status, data } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  return useMemo<GiftStoreAccessResult>(() => {
    if (!bugoId) {
      return { access: 'no-bugo', deceasedName: null };
    }

    // 조회 실패(형식이 깨진 id로 인한 CastError, 서버 오류 등) → 유효하지 않은 링크 안내.
    // (재시도는 기본값 유지 — 유효한 링크의 일시적 오류가 자가 복구되도록)
    if (status === 'error') {
      return { access: 'invalid', deceasedName: null };
    }

    // 로딩 중엔 판정 보류 (카탈로그/마감 화면이 번갈아 깜빡이는 것 방지)
    if (status === 'idle' || status === 'loading') {
      return { access: 'loading', deceasedName: null };
    }

    // 존재하지 않는 부고는 서버가 200 + null 을 반환한다 → 유효하지 않은 링크로 안내
    // (계속 로딩을 띄우거나 빈 카탈로그를 보여주지 않는다)
    if (!data) {
      return { access: 'invalid', deceasedName: null };
    }

    const deceasedInfo = data.fevent?.deceasedInfo;
    const deceasedName = deceasedInfo?.name ?? null;
    const rawCoffinOut = deceasedInfo?.coffinOut?.date ?? null;
    const coffinOutDate = rawCoffinOut ? new Date(rawCoffinOut) : null;

    // 발인일이 없거나 유효하지 않으면 열어둔다 (데이터 미비로 정상 유저를 막지 않음)
    if (!coffinOutDate || Number.isNaN(coffinOutDate.getTime())) {
      return { access: 'open', deceasedName };
    }

    const daysSinceCoffinOut = Math.floor(
      (startOfDay(new Date()) - startOfDay(coffinOutDate)) / MS_PER_DAY,
    );
    const access: GiftStoreAccessStatus =
      daysSinceCoffinOut > GIFT_STORE_SELF_ORDER_DAYS ? 'closed' : 'open';

    return { access, deceasedName };
  }, [bugoId, status, data]);
};
